/**
 * Skill tabs — show one faction skill's content at a time.
 *
 * Three surfaces present the same 5-skill set: the character page's Skills pane,
 * the Gear tab's Field Kit, and its Technique Consumables. All three used to
 * render every skill at once, which is what made those pages scroll several
 * screens deep on a tablet. This is the one strip implementation they share,
 * for the same reason subTabs.js exists — see that file's header.
 *
 * The distinction from subTabs.js is deliberate: that module toggles STATIC
 * panes by element id and clamps their scroll height. These panes are rebuilt
 * from data on every render, so there is nothing stable to toggle — the caller
 * re-renders and asks which skill to draw.
 *
 * Selection is module-scoped per group, so re-rendering (crafting a piece,
 * spending a point) keeps the player on the skill they were reading. Groups are
 * independent: the skill you are reading about is not necessarily the one whose
 * gear you are crafting.
 */

import * as config from '../constants.js';
import { gameState } from './gameState.js';

/** groupId -> selected skill key */
const selected = new Map();
/** groupId -> re-render callback, invoked on switch */
const renderers = new Map();

const skillKeysFor = (faction) => Object.keys(config.factionSkills[faction]?.skills || {});

/**
 * @param {string} groupId
 * @param {() => void} rerender - redraws the group's content for the new skill
 */
export function registerSkillTabs(groupId, rerender) {
    renderers.set(groupId, rerender);
}

/**
 * The group's selected skill, validated against the faction.
 *
 * The validation is what makes character switching safe: a Dominion skill key
 * left over from the previous character is not a Veil skill, and rendering it
 * would blank the pane rather than fall back.
 */
export function activeSkillTab(groupId, faction) {
    const keys = skillKeysFor(faction);
    const current = selected.get(groupId);
    return keys.includes(current) ? current : (keys[0] || null);
}

export function setSkillTab(groupId, skillKey) {
    selected.set(groupId, skillKey);
    renderers.get(groupId)?.();
}

/**
 * Inactive tabs keep only the first word: "System Override" -> "System…".
 *
 * Five full skill names do not fit one row on a tablet, and the first word is
 * what distinguishes them anyway (Signal / System / Drone / Safeguard /
 * Quantum). The full name stays in the title attribute.
 */
function tabLabel(name, isActive) {
    if (isActive) return name;
    const [first, ...rest] = name.split(' ');
    return rest.length ? `${first}…` : first;
}

/**
 * The strip: one tab per faction skill. Nothing but selection lives here —
 * the level and its upgrade button belong to the Skills pane's card, which is
 * the only one of the three surfaces where levelling up is the point.
 */
export function skillTabStrip(groupId, faction) {
    const skills = config.factionSkills[faction]?.skills || {};
    const active = activeSkillTab(groupId, faction);

    const tabs = Object.entries(skills).map(([skillKey, skill]) => {
        const isActive = skillKey === active;
        return `
            <button type="button"
                    class="skill-tab-btn tracking-tighter border-2 ${isActive ? 'active' : ''}"
                    data-skill-tab-group="${groupId}"
                    data-skill-tab="${skillKey}"
                    onclick="setSkillTab('${groupId}', '${skillKey}')"
                    title="${skill.name}">
                <span>${skill.icon || '⚡'}</span>
                <span class="skill-tab-name">${skill.name}</span>
            </button>`;
    }).join('');

    return `<div class="skill-tabs gap-1 mb-1">${tabs}</div>`;
}

if (typeof window !== 'undefined') {
    window.setSkillTab = setSkillTab;
}
